import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Custom navigation hook that preserves language query parameter
 *
 * Works with HashRouter by extracting and preserving the ?lang= parameter
 * from the current hash fragment.
 *
 * Returns a stable function reference (via useCallback) so it can safely
 * appear in useEffect dependency arrays without causing re-fires.
 *
 * Usage: const navigateWithLang = useNavigateWithLang()
 *        navigateWithLang('/rideshare/products')
 *        navigateWithLang(-1) // Go back
 */
export const useNavigateWithLang = () => {
  const navigate = useNavigate();

  return useCallback((to, options) => {
    // If navigating back/forward (number), just pass through
    if (typeof to === 'number') {
      return navigate(to, options);
    }

    // Extract current language parameter from hash (for HashRouter)
    const hash = window.location.hash;
    const queryStart = hash.indexOf('?');
    let currentLang = null;

    if (queryStart !== -1) {
      const queryString = hash.substring(queryStart + 1);
      const urlParams = new URLSearchParams(queryString);
      currentLang = urlParams.get('lang');
    }

    // If no language parameter found, just navigate normally
    if (!currentLang) {
      return navigate(to, options);
    }

    // Add language parameter to the new path
    const separator = to.includes('?') ? '&' : '?';
    const pathWithLang = `${to}${separator}lang=${currentLang}`;

    return navigate(pathWithLang, options);
  }, [navigate]);
};
